import styled from '@emotion/styled'
import { People } from '@material-ui/icons'
import { Box, Tooltip, Typography, tooltipClasses } from '@mui/material'
import { colors, typography, palette } from '@resiliatech/birdsong'

export const FunderGoalsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 0 26px 60px 20px;
`

export const HeaderContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
`

export const Content = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`

export const GoalsList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const Actions = styled(Box)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

export const Header = styled(Typography)`
  ${typography.up4.regularSerif}
  font-size: 34px;
`

export const GoalCardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isInteractive',
})<{ isInteractive?: boolean }>`
  cursor: ${({ isInteractive }) => (isInteractive ? 'pointer' : 'default')};
  display: flex;
  background-color: ${colors.background.secondaryBackground};
  border-radius: 8px;
  gap: 16px;
  justify-content: space-between;
  padding: 24px;
  position: relative;
  width: 100%;

  &:hover {
    background-color: ${({ isInteractive }) =>
      isInteractive ? colors.interactive.secondaryHoverFocus : 'none'};
  }
`
export const EmptyGoalGroupCardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isInteractive',
})<{ isInteractive?: boolean }>`
  cursor: ${({ isInteractive }) => (isInteractive ? 'pointer' : 'default')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${colors.feedback.errorLight};
  border-radius: 8px;
  gap: 16px;
  padding: 24px;
  position: relative;
  width: 100%;
  height: 245px;
  text-align: center;

  &:hover {
    background-color: ${({ isInteractive }) =>
      isInteractive ? colors.interactive.secondaryHoverFocus : 'none'};
  }
`

export const Details = styled(Box)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: space-between;
  width: 100%;
`

export const TopSection = styled(Box)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

export const EmptyGoalGroupSection = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  background-color: ${colors.feedback.errorLight};
`

export const Category = styled(Box)`
  ${typography.down2.regularSans}
  color: ${colors.feedback.infoDark};
  text-transform: uppercase;
  letter-spacing: 1px;
`

export const CategoryAndStatus = styled(Box)`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 6px;
`

export const GoalTitle = styled(Typography)`
  ${typography.up2.regularSerif}
  font-family: Merriweather;
  color: ${colors.copy.headerDark};
  text-align: left;
  margin-right: 8px;
`

export const GoalDescription = styled(Typography)`
  ${typography.baseline.regularSans}
  color: ${palette.text.secondary};
  text-align: left;
  word-break: break-word;
`

export const TaskInformation = styled(Box)`
  display: flex;
  gap: 10px;
`

const chipStatusColors = {
  completed: {
    background: colors.feedback.successLight,
    text: colors.text.primary,
  },
  informational: {
    background: colors.feedback.infoLight,
    text: colors.feedback.infoDark,
  },
  shared: {
    background: colors.warning.light,
    text: colors.text.primary,
  },
  granteeList: {
    background: colors.action.selected,
    text: colors.text.primary,
  },
  warning: {
    background: colors.feedback.errorLight,
    text: colors.feedback.errorDark,
  },
}

export const TaskStatus = styled(Box)<{
  variant?: 'completed' | 'informational' | 'warning' | 'shared'
}>`
  align-items: center;
  background-color: ${({ variant }) =>
    variant && chipStatusColors[variant].background};
  border: ${({ variant }) =>
    variant
      ? `1px solid ${chipStatusColors[variant].background}`
      : `1px solid ${colors.interactive.disabledLight}`};
  border-radius: 4px;
  color: ${({ variant }) => variant && chipStatusColors[variant].text};
  display: flex;
  gap: 6px;
  padding: 6px;
`

export const GroupCardChips = styled(Box)`
  display: flex;
  gap: 10px;
  justify-content: space-between;
`

export const LeftChipsWrapper = styled(Box)`
  display: flex;
  gap: 10px;
`
export const ChipsWrapper = styled(Box)`
  display: flex;
  gap: 10px;
  justify-content: space-between;
  width: 100%;
  margin-top: 24px;
`

export const GranteeTotal = styled(Box)<{
  variant?: 'informational' | 'shared' | 'granteeList'
}>`
  align-items: center;
  background-color: ${({ variant }) =>
    variant && chipStatusColors[variant].background};
  border: ${({ variant }) =>
    variant
      ? `1px solid ${chipStatusColors[variant].background}`
      : `1px solid ${colors.interactive.disabledLight}`};
  border-radius: 4px;
  color: ${({ variant }) => variant && chipStatusColors[variant].text};
  display: flex;
  gap: 6px;
  padding: 6px;
`

export const TargetProgress = styled(Box)<{
  variant?: 'completed' | 'informational' | 'warning'
}>`
  align-items: center;
  background-color: ${({ variant }) =>
    variant && chipStatusColors[variant].background};
  border: ${({ variant }) =>
    variant
      ? `1px solid ${chipStatusColors[variant].background}`
      : `1px solid ${colors.interactive.disabledLight}`};
  border-radius: 4px;
  color: ${({ variant }) =>
    variant
      ? `1px solid ${chipStatusColors[variant].background}`
      : `1px solid ${colors.interactive.disabledLight}`};
  color: ${({ variant }) => variant && chipStatusColors[variant].text};
  display: flex;
  gap: 6px;
  padding: 6px;
`

export const Dates = styled(Box)`
  ${typography.down2.regularSans}

  align-items: center;
  color: ${palette.text.secondary};
  display: flex;
  gap: 8px;
`

export const DateInfo = styled(Box)`
  align-items: center;
  display: flex;
  gap: 12px;
`

export const Date = styled(Typography)`
  ${typography.down2.regularSans}
`

export const GoalProgressContainer = styled(Box)`
  display: inline-flex;
  position: relative;
`
export const GoalProgressLabelContainer = styled(Box)`
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`

export const EditOptions = styled(Box)`
  display: flex;
`
export const AvatarInformation = styled(Box)`
  align-self: flex-end;
  display: flex;
  height: min-content;
`

export const Avatars = styled(Box)`
  position: absolute;
  right: 24px;
  bottom: 24px;
`

export const AvatarTooltip = styled(Tooltip)`
  & .${tooltipClasses.tooltip} {
    background-color: ${colors.background.secondaryBackground};
    color: ${colors.common.white};
    font-size: 14px;
  }

  & .${tooltipClasses.arrow} {
    color: rgba(0, 0, 0, 0.8);
  }
`

export const FunderGranteeCount = styled.span`
  margin-left: -3px;
  color: ${colors.text.primary};
`
export const PeopleIcon = styled(People)`
  color: ${colors.text.primary};
  cursor: pointer;
  font-size: 20px;
  margin-right: 10px;
`
export const NonprofitNameText = styled.span`
  font-weight: 400;
  margin-left: -3px;
`
export const TinyDot = styled.span<{ color?: string }>`
  display: inline-block;
  background-color: ${(props) => props.color || colors.text.secondary};
  width: 3px;
  height: 3px;
  border-radius: 50%;
`

export const CategoryContainer = styled(Box)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`
